import { formatInTimeZone } from 'date-fns-tz';
import moment from 'moment-timezone';

const validateNamedTimeZone = (value) => {
  if (typeof value !== 'string' || !value.trim() || !moment.tz.zone(value)) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: value })
      .resolvedOptions()
      .timeZone;
  } catch {
    return null;
  }
};

const parseOffsetMinutes = (value) => {
  if (value === 'Z') return 0;
  const match = String(value || '').match(/^([+-])(\d{2}):(\d{2})$/);
  if (!match) return null;

  const minutes = (Number(match[2]) * 60) + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
};

const offsetAt = (instant, timeZone) => {
  try {
    return parseOffsetMinutes(formatInTimeZone(instant, timeZone, 'xxx'));
  } catch {
    return null;
  }
};

/**
 * Interpret the persisted trip date and departure time as an entity-local wall
 * time. Moment is intentionally shared with the backend policy: during a DST
 * spring gap it advances to the first representable wall time instead of
 * silently using the browser's timezone or a different library policy.
 */
export const buildEntityScheduledDepartureDate = (tripDate, departureTime, timeZone) => {
  const validatedTimeZone = validateNamedTimeZone(timeZone);
  const dateMatch = String(tripDate || '').match(/^(\d{4}-\d{2}-\d{2})(?:T.*)?$/);
  const timeMatch = String(departureTime || '').match(/^(\d{2}:\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (!validatedTimeZone || !dateMatch || !timeMatch) {
    return null;
  }

  const normalizedTime = `${timeMatch[1]}:${timeMatch[2] || '00'}`;
  const scheduled = moment.tz(
    `${dateMatch[1]} ${normalizedTime}`,
    'YYYY-MM-DD HH:mm:ss',
    true,
    validatedTimeZone
  );

  return scheduled.isValid() ? scheduled.toDate() : null;
};

/**
 * Resolve the additive authoritative settings contract. IANA identity alone
 * does not decide whether a notice is useful: equivalent profiles such as
 * America/New_York and America/Toronto are compared by their effective offset
 * at this trip's scheduled departure instant.
 */
export const resolveEntityTimeZone = (
  entitySettings,
  { tripDate, departureTime, browserTimeZone } = {}
) => {
  if (!Object.prototype.hasOwnProperty.call(entitySettings || {}, 'time_zone_reference')) {
    return {
      status: 'pending',
      timeZone: null,
      browserTimeZone: null,
      differsFromBrowser: false,
      label: null
    };
  }

  const reference = entitySettings.time_zone_reference;
  const timeZone = validateNamedTimeZone(reference?.iana_name);
  const label = typeof reference?.display_name === 'string' && reference.display_name.trim()
    ? reference.display_name.trim()
    : null;
  if (!timeZone || !label) {
    return {
      status: 'unavailable',
      timeZone: null,
      browserTimeZone: null,
      differsFromBrowser: false,
      label: null
    };
  }

  const resolvedBrowserTimeZone = validateNamedTimeZone(
    browserTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const scheduledDeparture = buildEntityScheduledDepartureDate(
    tripDate,
    departureTime,
    timeZone
  );
  const entityOffset = scheduledDeparture ? offsetAt(scheduledDeparture, timeZone) : null;
  const browserOffset = scheduledDeparture && resolvedBrowserTimeZone
    ? offsetAt(scheduledDeparture, resolvedBrowserTimeZone)
    : null;

  return {
    status: 'ready',
    timeZone,
    browserTimeZone: resolvedBrowserTimeZone,
    // If the comparison cannot be proved, retain the valid entity-zone display
    // and suppress only the optional notice.
    differsFromBrowser: entityOffset !== null
      && browserOffset !== null
      && entityOffset !== browserOffset,
    label
  };
};
